import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';

export type ResizableTelemetryColumn = {
  id: string;
  label: ReactNode;
  resizeLabel: string;
  width: number;
  minWidth?: number;
};

type ResizableTelemetryTableProps = {
  columns: ResizableTelemetryColumn[];
  children: ReactNode;
  className?: string;
};

type ColumnDefault = {
  width: number;
  minWidth: number;
};

const defaultMinWidth = (column: ResizableTelemetryColumn): number => column.minWidth ?? 72;
const clampedDefaultWidth = (column: ResizableTelemetryColumn): number => Math.max(defaultMinWidth(column), column.width);

const ResizableTelemetryTable = ({ columns, children, className = '' }: ResizableTelemetryTableProps) => {
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() =>
    Object.fromEntries(columns.map((column) => [column.id, clampedDefaultWidth(column)]))
  );
  const columnDefaultsRef = useRef<Record<string, ColumnDefault>>(
    Object.fromEntries(columns.map((column) => [column.id, { width: column.width, minWidth: defaultMinWidth(column) }]))
  );
  const dragRef = useRef<{ id: string; startX: number; startWidth: number; minWidth: number } | null>(null);
  const cleanupDragRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    setColumnWidths((current) => {
      let changed = false;
      const next: Record<string, number> = {};
      const nextDefaults: Record<string, ColumnDefault> = {};
      for (const column of columns) {
        const minWidth = defaultMinWidth(column);
        const previousDefault = columnDefaultsRef.current[column.id];
        nextDefaults[column.id] = { width: column.width, minWidth };
        if (!previousDefault || previousDefault.width !== column.width || previousDefault.minWidth !== minWidth) {
          next[column.id] = clampedDefaultWidth(column);
          changed = true;
        } else {
          next[column.id] = Math.max(minWidth, current[column.id] ?? clampedDefaultWidth(column));
          changed ||= next[column.id] !== current[column.id];
        }
      }
      changed ||= Object.keys(current).some((id) => !nextDefaults[id]);
      columnDefaultsRef.current = nextDefaults;
      return changed ? next : current;
    });
  }, [columns]);

  useEffect(() => () => {
    cleanupDragRef.current?.();
  }, []);

  const beginResize = (column: ResizableTelemetryColumn, event: ReactPointerEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    cleanupDragRef.current?.();
    const startWidth = columnWidths[column.id] ?? clampedDefaultWidth(column);
    const minWidth = column.minWidth ?? 72;
    dragRef.current = { id: column.id, startX: event.clientX, startWidth, minWidth };

    const handlePointerMove = (moveEvent: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag) {
        return;
      }
      const nextWidth = Math.max(drag.minWidth, Math.round(drag.startWidth + moveEvent.clientX - drag.startX));
      setColumnWidths((current) => ({ ...current, [drag.id]: nextWidth }));
    };

    const cleanupListeners = () => {
      dragRef.current = null;
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
      cleanupDragRef.current = null;
    };

    const handlePointerUp = () => {
      cleanupListeners();
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);
    cleanupDragRef.current = cleanupListeners;
  };

  const resizeColumnWithKeyboard = (column: ResizableTelemetryColumn, event: ReactKeyboardEvent<HTMLButtonElement>) => {
    const minWidth = defaultMinWidth(column);
    const step = event.shiftKey ? 48 : 16;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      setColumnWidths((current) => ({
        ...current,
        [column.id]: Math.max(minWidth, (current[column.id] ?? column.width) - step)
      }));
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      setColumnWidths((current) => ({
        ...current,
        [column.id]: (current[column.id] ?? clampedDefaultWidth(column)) + step
      }));
    } else if (event.key === 'Home') {
      event.preventDefault();
      setColumnWidths((current) => ({
        ...current,
        [column.id]: clampedDefaultWidth(column)
      }));
    }
  };

  const minWidth = columns.reduce((total, column) => total + (columnWidths[column.id] ?? clampedDefaultWidth(column)), 0);

  return (
    <div className="telemetry-inspector__mini-table-wrap">
      <table className={`telemetry-inspector__mini-table ${className}`} style={{ minWidth }}>
        <colgroup>
          {columns.map((column) => (
            <col key={column.id} style={{ width: columnWidths[column.id] ?? clampedDefaultWidth(column) }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.id}>
                <span className="telemetry-inspector__resize-label">{column.label}</span>
                <button
                  type="button"
                  className="telemetry-inspector__column-resizer"
                  aria-label={`Resize ${column.resizeLabel} column`}
                  title={`Resize ${column.resizeLabel} column`}
                  aria-keyshortcuts="ArrowLeft ArrowRight Home"
                  onPointerDown={(event) => beginResize(column, event)}
                  onKeyDown={(event) => resizeColumnWithKeyboard(column, event)}
                />
              </th>
            ))}
          </tr>
        </thead>
        {children}
      </table>
    </div>
  );
};

export default ResizableTelemetryTable;
